import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export function PrivacyPolicyPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const handleBack = () => {
    if (token) {
      navigate(`/${token}`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-sky-100 dark:bg-sky-900/20 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Política de Privacidad
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Última actualización: Noviembre 2025
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          <div className="prose dark:prose-invert max-w-none">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              1. Información que Recopilamos
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              ComandaYa recopila la siguiente información personal:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-6 space-y-2">
              <li>Correo electrónico o número de teléfono (para identificación de sesión)</li>
              <li>Información de pedidos realizados durante tu visita</li>
              <li>Datos técnicos de navegación (cookies, dirección IP, tipo de dispositivo)</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              2. Uso de la Información
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Utilizamos tu información exclusivamente para:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-6 space-y-2">
              <li>Identificar y gestionar tu sesión en la mesa</li>
              <li>Procesar y gestionar tus pedidos</li>
              <li>Facilitar el proceso de pago</li>
              <li>Mejorar nuestros servicios y experiencia de usuario</li>
              <li>Cumplir con obligaciones legales y fiscales</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              3. No Compartimos tus Datos
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              <strong>ComandaYa no comparte, vende ni alquila tu información personal a terceros.</strong> Tus datos son utilizados únicamente dentro de nuestra plataforma y el establecimiento donde realizaste tu pedido. Solo compartimos la información estrictamente necesaria con:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-6 space-y-2">
              <li>El establecimiento gastronómico donde realizas tu pedido (para procesar tu orden)</li>
              <li>Procesadores de pago (solo para transacciones financieras seguras)</li>
              <li>Autoridades legales cuando sea requerido por ley</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              4. Seguridad de los Datos
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida, destrucción o alteración. Esto incluye cifrado de datos, acceso restringido y monitoreo continuo de nuestros sistemas.
            </p>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              5. Retención de Datos
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Tu información de sesión se mantiene activa únicamente durante tu visita al establecimiento. Una vez que cierres tu sesión o finalices tu visita, los datos de identificación son anonimizados. Los datos de pedidos pueden conservarse por períodos más largos por razones legales, fiscales o de auditoría.
            </p>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              6. Cookies y Tecnologías Similares
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              ComandaYa utiliza cookies y tecnologías similares para mejorar la experiencia del usuario, mantener tu sesión activa y analizar el uso de la plataforma. Puedes configurar tu navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del servicio.
            </p>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              7. Tus Derechos
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Tienes derecho a:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-6 space-y-2">
              <li>Acceder a tu información personal</li>
              <li>Solicitar la corrección de datos inexactos</li>
              <li>Solicitar la eliminación de tus datos (sujeto a obligaciones legales)</li>
              <li>Oponerte al procesamiento de tus datos</li>
              <li>Solicitar la portabilidad de tus datos</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              8. Cambios en la Política de Privacidad
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              ComandaYa se reserva el derecho de actualizar esta política de privacidad en cualquier momento. Te notificaremos sobre cambios significativos mediante un aviso en la plataforma o por correo electrónico.
            </p>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              9. Contacto
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Si tienes preguntas sobre esta política de privacidad o deseas ejercer tus derechos sobre tus datos personales, puedes contactarnos a través del establecimiento o directamente con el soporte de ComandaYa.
            </p>

            <div className="mt-8 p-4 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-200 dark:border-sky-800">
              <p className="text-sm text-sky-800 dark:text-sky-200 font-medium">
                💡 Recuerda: ComandaYa utiliza tu correo o teléfono solo para identificar tu sesión dentro del sistema. No compartimos tus datos con terceros para fines comerciales o de marketing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
